import { useEffect, useRef, useState } from "react";
import { pixelsToPng, prepareImage, protectedFilename } from "./lib/image";
import { protectImage, type ProtectionProgress } from "./lib/protection";

type Strength = { label: string; value: number; note: string };

const strengths: Strength[] = [
  { label: "Subtle", value: 0.55, note: "Least visible" },
  { label: "Balanced", value: 0.78, note: "Recommended" },
  { label: "Strong", value: 1, note: "Maximum layer" },
];

function ShieldIcon({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2.8 20 6v5.6c0 4.7-3.2 8.1-8 9.7-4.8-1.6-8-5-8-9.7V6l8-3.2Z" stroke="currentColor" strokeWidth="1.8" />
      <path d="m8.7 12 2.1 2.1 4.7-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 15V4m0 0L8 8m4-4 4 4M5 14v4.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 12h14m-5-5 5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 4v11m0 0 4-4m-4 4-4-4M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function App() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [strength, setStrength] = useState(strengths[1]);
  const [progress, setProgress] = useState<ProtectionProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [duration, setDuration] = useState<number | null>(null);
  const isProcessing = progress !== null && progress.progress < 100;

  useEffect(() => {
    return () => {
      if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    };
  }, [sourceUrl]);

  useEffect(() => {
    return () => {
      if (resultUrl) URL.revokeObjectURL(resultUrl);
    };
  }, [resultUrl]);

  function chooseFile(next: File | undefined) {
    if (!next) return;
    if (!next.type.startsWith("image/")) {
      setError("Choose a PNG, JPEG, or WebP image.");
      return;
    }
    if (next.size > 40 * 1024 * 1024) {
      setError("Choose an image smaller than 40 MB.");
      return;
    }

    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    if (resultUrl) URL.revokeObjectURL(resultUrl);
    setFile(next);
    setSourceUrl(URL.createObjectURL(next));
    setResultUrl(null);
    setResultBlob(null);
    setDuration(null);
    setError(null);
    setProgress(null);
  }

  async function processImage() {
    if (!file || isProcessing) return;
    setError(null);
    setResultUrl(null);
    setResultBlob(null);
    setProgress({ message: "Reading your image locally", progress: 6 });

    try {
      const prepared = await prepareImage(file);
      if (prepared.width * prepared.height > 24_000_000) {
        throw new Error("This image is over 24 megapixels. Resize it and try again.");
      }

      const result = await protectImage(
        {
          ...prepared,
          strength: strength.value,
        },
        setProgress,
      );

      setProgress({
        message: "Finishing the protected PNG",
        progress: 94,
        backend: result.backend,
      });
      const blob = await pixelsToPng(result.pixels, prepared.width, prepared.height);
      const url = URL.createObjectURL(blob);
      setResultBlob(blob);
      setResultUrl(url);
      setDuration(result.durationMs);
      setProgress({
        message: "Protection complete",
        progress: 100,
        backend: result.backend,
      });
    } catch (caught) {
      setProgress(null);
      setError(caught instanceof Error ? caught.message : "The image could not be processed.");
    }
  }

  function downloadResult() {
    if (!resultUrl || !resultBlob || !file) return;
    const anchor = document.createElement("a");
    anchor.href = resultUrl;
    anchor.download = protectedFilename(file.name);
    anchor.click();
  }

  return (
    <div className="site-shell">
      <header className="nav-wrap">
        <a className="brand" href="#top" aria-label="Veil home">
          <span className="brand-mark"><ShieldIcon /></span>
          <span>Veil</span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#how-it-works">How it works</a>
          <a href="#principles">Principles</a>
          <a className="nav-cta" href="#protect">Protect an image <ArrowIcon /></a>
        </nav>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-copy">
            <div className="eyebrow"><span /> Local-first image protection</div>
            <h1>Put a veil over<br />your <em>artwork.</em></h1>
            <p className="hero-lede">
              Apply an experimental protective layer against unauthorized diffusion-based editing. Processing stays on your device—always.
            </p>
            <div className="hero-actions">
              <a className="button button-dark" href="#protect">Protect an image <ArrowIcon /></a>
              <a className="text-link" href="#how-it-works">See how it works</a>
            </div>
            <div className="privacy-line"><LockIcon /> No uploads. No accounts. No tracking.</div>
          </div>

          <div className="hero-art" aria-hidden="true">
            <div className="art-frame">
              <div className="sun-disc" />
              <div className="hill hill-one" />
              <div className="hill hill-two" />
              <div className="art-grid" />
              <div className="art-label">YOUR IMAGE<br /><span>STAYS YOURS</span></div>
            </div>
            <div className="privacy-stamp"><ShieldIcon size={28} /><span>LOCAL<br />ONLY</span></div>
          </div>
        </section>

        <section className="protector-section" id="protect">
          <div className="section-heading">
            <span className="section-number">01</span>
            <div>
              <p className="kicker">Browser-based protection</p>
              <h2>Your image never leaves this page.</h2>
            </div>
            <p className="section-aside">The model downloads to your browser and runs locally with WebGPU or WebAssembly.</p>
          </div>

          <div className="tool-card">
            <div className="tool-main">
              {!file ? (
                <button
                  className={`drop-zone ${isDragging ? "is-dragging" : ""}`}
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                    chooseFile(event.dataTransfer.files[0]);
                  }}
                >
                  <span className="upload-icon"><UploadIcon /></span>
                  <strong>Drop your artwork here</strong>
                  <span>or click to choose a file</span>
                  <small>PNG, JPEG or WebP · up to 40 MB</small>
                </button>
              ) : (
                <div className="workspace">
                  <div className="image-stage">
                    <div className="stage-tabs" aria-label="Image preview state">
                      <span className={!resultUrl ? "active" : ""}>Original</span>
                      <span className={resultUrl ? "active" : ""}>Protected</span>
                    </div>
                    <img src={resultUrl ?? sourceUrl ?? ""} alt={resultUrl ? "Protected artwork preview" : "Original artwork preview"} />
                    {isProcessing && (
                      <div className="processing-overlay">
                        <div className="loader-ring" />
                        <strong>{progress?.message}</strong>
                        <span>{progress?.progress ?? 0}%</span>
                      </div>
                    )}
                  </div>
                  <div className="file-row">
                    <div>
                      <strong>{file.name}</strong>
                      <span>{(file.size / 1024 / 1024).toFixed(1)} MB</span>
                    </div>
                    <button type="button" onClick={() => inputRef.current?.click()} disabled={isProcessing}>Replace</button>
                  </div>
                </div>
              )}

              <input
                ref={inputRef}
                className="visually-hidden"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(event) => chooseFile(event.target.files?.[0])}
              />
            </div>

            <aside className="tool-controls">
              <div>
                <div className="control-label"><span>Protection strength</span><span>{strength.label}</span></div>
                <div className="strength-options">
                  {strengths.map((option) => (
                    <button
                      type="button"
                      key={option.label}
                      className={strength.label === option.label ? "selected" : ""}
                      onClick={() => setStrength(option)}
                      disabled={isProcessing}
                    >
                      <span className="radio-dot" />
                      <span><strong>{option.label}</strong><small>{option.note}</small></span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="model-note">
                <span className="model-icon"><ShieldIcon size={20} /></span>
                <div><strong>DiffVax · experimental</strong><span>Single-pass diffusion editing defense</span></div>
              </div>

              {error && <div className="error-message" role="alert">{error}</div>}

              {resultUrl ? (
                <>
                  <button className="button button-accent full" type="button" onClick={downloadResult}>
                    Download protected PNG <DownloadIcon />
                  </button>
                  <button className="button button-outline full" type="button" onClick={processImage}>
                    Process again
                  </button>
                  <p className="result-meta">
                    Completed locally in {duration ? `${(duration / 1000).toFixed(1)}s` : "your browser"}
                    {progress?.backend ? ` · ${progress.backend.toUpperCase()}` : ""}
                  </p>
                </>
              ) : (
                <button className="button button-accent full" type="button" onClick={processImage} disabled={!file || isProcessing}>
                  {isProcessing ? "Processing locally…" : "Apply protection"} <ArrowIcon />
                </button>
              )}
            </aside>
          </div>
          <p className="tool-disclaimer">
            Experimental protection is not a guarantee against copying, model training, or future countermeasures. Keep your original file private.
          </p>
        </section>

        <section className="how-section" id="how-it-works">
          <div className="section-heading light">
            <span className="section-number">02</span>
            <div>
              <p className="kicker">A quiet layer of resistance</p>
              <h2>Visible to models.<br />Barely visible to you.</h2>
            </div>
          </div>
          <div className="steps-grid">
            <article><span>1</span><h3>Choose your work</h3><p>Your browser reads the image directly from your device. It is never sent to us.</p></article>
            <article><span>2</span><h3>Generate the layer</h3><p>A compact local model creates a subtle perturbation aimed at diffusion editing systems.</p></article>
            <article><span>3</span><h3>Save the public copy</h3><p>Download a new PNG for sharing. Keep the untouched original in your own archive.</p></article>
          </div>
        </section>

        <section className="principles-section" id="principles">
          <div className="principles-title">
            <p className="kicker">Built around artist agency</p>
            <h2>Protection without taking possession.</h2>
          </div>
          <div className="principles-list">
            <div><span>01</span><h3>Local by design</h3><p>Inference and image processing happen inside your browser.</p></div>
            <div><span>02</span><h3>Open foundations</h3><p>Built from auditable MIT-licensed research and runtime components.</p></div>
            <div><span>03</span><h3>Honest boundaries</h3><p>No “AI-proof” promises. The protection target and limitations stay visible.</p></div>
          </div>
        </section>
      </main>

      <footer>
        <div className="brand"><span className="brand-mark"><ShieldIcon /></span><span>Veil</span></div>
        <p>Experimental local image immunization for artists.</p>
        <div><a href="/THIRD_PARTY_NOTICES.txt">Licenses</a><a href="https://github.com/ozdentarikcan/DiffVax">Research source</a></div>
      </footer>
    </div>
  );
}

export default App;
